import React, { useEffect, useRef, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { Store__System } from "@talentbait/shared-components";

import { CartIcon, CheckAssistantIcon } from "../../../Components/Icons";

import {
  CartBox,
  CartItem,
  CartLayout,
  SectionSummary,
  MinBadget,
  ConfettiContainer,
  SectionCounter,
  AdCounterWrapper,
  CartCounter,
  SectionTitle,
  ScrollContainer,
  ItemsContainer,
  CartItemWrapper,
  SliderArrow,
  MinBadgetWrapper
} from "./Cart.styled";
import { FlexWrapper } from "../AdAssistant.styled";
import { BlueLeftArrow } from "../../../Components/Icons/BlueLeftArrow";
import { BlueRightArrow } from "../../../Components/Icons/BlueRightArrow";
import { withRefluxStore } from "../../../Stores";
import AdAssistantStore from "../AdAssistantStore";
import { ActionsAdAssistant } from "../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import { useAdUpdate } from "../hooks/useAdUpdate";

const Cart = ({
  requiredItems = 3,
  selectedItems = 0,
  showSelectedBox = true,
  //Store
  userProfile,
  campaignData,
  shutterstockImages = 0,
  shutterstockVideos = 0,
  totalShutterstockAssetsPrice = 0,
  usedAds = [],
  currentAd = {},
  allAdCombinations = []
}) => {
  const { t } = useTranslation();
  const { findNextAvailableAd } = useAdUpdate(currentAd, allAdCombinations, userProfile);

  const confettiShown = useRef(false);
  const containerRef = useRef(null);
  const [showMinBadgets, setShowMinBadgets] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Calculate number of items to show (10 by default, or selectedItems + 5)
  const itemsToShow = Math.max(selectedItems + 5, 10);
  const itemsArray = Array.from({ length: itemsToShow }, (_, index) => index);

  const reachedMinimum = selectedItems >= requiredItems;

  const handleAnimationEnd = () => {
    confettiShown.current = false;
    setShowConfetti(false);
  };

  useEffect(() => {
    const blockConfetti = campaignData.status !== "NEW" && campaignData.status !== "DRAFT";

    if (reachedMinimum && !confettiShown.current && !blockConfetti) {
      confettiShown.current = true;
      setShowConfetti(true);
    }
  }, [reachedMinimum, campaignData.status]);

  useEffect(() => {
    const index = usedAds.findIndex((ad) => ad.id === currentAd.id);
    setCurrentIndex(index !== -1 ? index : usedAds.length);
  }, [usedAds, currentAd]);

  const checkScroll = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      setShowMinBadgets(container.scrollLeft < 10);
    }
  };

  const handleScroll = (direction) => {
    if (containerRef.current) {
      const container = containerRef.current;
      const scrollAmount = 100;

      // Calculate new index based on direction
      const newIndex = direction === "left" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex >= 0 && newIndex < itemsArray.length) {
        handleSelectAd(newIndex);
      }

      container.scrollTo({
        left: container.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth"
      });

      setTimeout(checkScroll, 300);
    }
  };

  useEffect(() => {
    checkScroll();
  }, [itemsArray.length]);

  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const itemWidth = 29;
      const scrollPosition = Math.max(0, (currentIndex - 3) * itemWidth);

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });

      setTimeout(checkScroll, 300);
    }
  }, [currentIndex]);

  const handleSelectAd = (index) => {
    const lookForNextAd = index >= usedAds.length;

    if (lookForNextAd) {
      setCurrentIndex(usedAds.length);
      findNextAvailableAd(allAdCombinations);
    } else {
      setCurrentIndex(index);
      ActionsAdAssistant.setCurrentAd(usedAds[index]);
    }
  };

  const isFirstAdBox = currentIndex === 0;
  const isLastAdBox = currentIndex === usedAds.length;

  return (
    <CartLayout>
      <CartBox>
        <FlexWrapper
          gap={10}
          direction="column"
          wrap="wrap"
          justify="flex-start"
          align="flex-start"
        >
          <SectionTitle gap={10}>
            {t("Selected ads")} <SectionCounter>{selectedItems}</SectionCounter>
          </SectionTitle>
        </FlexWrapper>
        {showSelectedBox && (
          <AdCounterWrapper
            gap={5}
            direction="column"
            justify="flex-start"
            align="flex-start"
            $hasMinBadgets={showMinBadgets}
          >
            <MinBadgetWrapper gap={29} wrap="wrap" show={showMinBadgets}>
              {Array.from({ length: requiredItems }, (_, index) => {
                const isMinimumIndex = index === 2;
                return (
                  <MinBadget
                    key={index}
                    $isMinBadget={isMinimumIndex}
                    $reachedMinimum={reachedMinimum}
                  >
                    {showConfetti && isMinimumIndex && (
                      <ConfettiContainer onAnimationEnd={handleAnimationEnd}>
                        <img src="/images/confetti.gif" alt="confetti" />
                      </ConfettiContainer>
                    )}
                    {isMinimumIndex && "Min."}
                  </MinBadget>
                );
              })}
            </MinBadgetWrapper>
            <ScrollContainer>
              <SliderArrow
                direction="left"
                onClick={() => handleScroll("left")}
                disabled={isFirstAdBox}
                $isFirstAdBox={isFirstAdBox}
              >
                <BlueLeftArrow size={24} color="var(--primary-blue)" />
              </SliderArrow>

              <ItemsContainer ref={containerRef} onScroll={checkScroll}>
                {itemsArray.map((index) => {
                  const isSelected = index < selectedItems;
                  const reachedMinimumAndSelected = reachedMinimum && isSelected;

                  return (
                    <CartItemWrapper
                      key={index}
                      initial={{ y: 0 }}
                      animate={isSelected ? { y: [0, -8, 0] } : {}}
                      transition={{
                        duration: 0.5,
                        repeat: 1,
                        ease: "easeInOut"
                      }}
                    >
                      <CartItem
                        $isRequired={index < requiredItems}
                        $isSelected={isSelected}
                        $reachedMinimum={reachedMinimum}
                        $isCurrent={index === currentIndex}
                        $isNext={index === usedAds.length}
                        onClick={() => handleSelectAd(index)}
                      >
                        {isSelected && (
                          <CheckAssistantIcon
                            color={reachedMinimumAndSelected ? "#1E1F43" : "#fff"}
                          />
                        )}
                      </CartItem>
                    </CartItemWrapper>
                  );
                })}
              </ItemsContainer>

              <SliderArrow
                direction="right"
                onClick={isLastAdBox ? null : () => handleScroll("right")}
                disabled={isLastAdBox}
                $isLastAdBox={isLastAdBox}
              >
                <BlueRightArrow
                  size={24}
                  color={isLastAdBox ? "var(--primary-dark-200)" : "var(--primary-blue)"}
                />
              </SliderArrow>
            </ScrollContainer>
          </AdCounterWrapper>
        )}
        <FlexWrapper gap={10} justify="flex-end" width="100%">
          <FlexWrapper gap={10} justify="flex-start" width="100%">
            <SectionSummary gap={10}>
              <CartIcon />
              <SectionSummary gap={4}>
                <span>{t("Images")}</span>
                <CartCounter>{shutterstockImages}</CartCounter>
              </SectionSummary>
            </SectionSummary>
            <SectionSummary gap={4}>
              <span>{t("Videos")}</span>
              <CartCounter>{shutterstockVideos}</CartCounter>
            </SectionSummary>
          </FlexWrapper>
          <SectionSummary gap={4} style={{ justifyContent: "flex-end" }}>
            <span>{t("Total")}</span>
            <CartCounter>{totalShutterstockAssetsPrice} €</CartCounter>
          </SectionSummary>
        </FlexWrapper>
      </CartBox>
    </CartLayout>
  );
};

export default memo(
  withRefluxStore(
    Cart,
    [AdAssistantStore, Store__System],
    [
      "campaignData",
      "shutterstockImages",
      "shutterstockVideos",
      "totalShutterstockAssetsPrice",
      "usedAds",
      "currentAd",
      "allAdCombinations",
      "userProfile"
    ]
  )
);
